/**
 * Heating / steam table row (e.g. Tsuneishi HEATING TABLE).
 */
export interface SteamTableRowConfig {
  tankName: string;
  /** Tank capacity (m³ or as provided in source table) */
  capacity: number;
  /** Heating Surface (m²) from the Heating Grid Pipe column */
  heatingSurfaceM2?: number | null;
  /** Max Steam from the heating table (kg/hr or MT as in source) */
  maxSteam?: number | null;
  stepRequirement?: string | number | null;
  /** Legacy column — not used for display; kept for old imports only */
  heatingRatio?: number | null;
}

/** Row for the 4-column heating table UI (includes optional TOTAL row). */
export interface HeatingTableDisplayRow extends SteamTableRowConfig {
  isTotal?: boolean;
  /** Exact value from source — shown 1:1 on UI */
  capacityDisplay?: string;
  heatingSurfaceDisplay?: string;
  maxSteamDisplay?: string;
}

export const HEATING_TABLE_UI_COLUMNS = [
  { key: 'tankName', label: 'Tank Name' },
  { key: 'capacity', label: 'Tank Capacity' },
  {
    key: 'heatingSurfaceM2',
    label: 'Heating Grid Pipe – Heating Surface (m²)',
  },
  { key: 'maxSteam', label: 'Max Steam Consumption (kg/hr)' },
] as const;

export interface SteamTableExtractionConfig {
  fileNamePattern?: RegExp;
  label?: string;
  rows: SteamTableRowConfig[];
}

export const CARGO_TANK_STEP_REQUIREMENT = '44°C to 66°C within 96hrs';
export const SLOP_TANK_STEP_REQUIREMENT = '15°C to 66°C within 24hrs';

function surfaceFromRatio(capacity: number, ratio: number): number {
  return Math.round(capacity * ratio * 100) / 100;
}

/**
 * Reference data from HEATING TABLE (TSUNEISHI 106,300 MT CRUDE TANKER).
 * Heating surface derived from documented ratio × capacity where max steam is unavailable.
 */
/** Max steam consumption (kg/hr) — HEATING GRID PIPE column (106,300 MT crude tanker). */
export const HEATING_TABLE_TSUNEISHI_1230: SteamTableRowConfig[] = [
  {
    tankName: 'NO. 1 COT (P)',
    capacity: 8245,
    heatingSurfaceM2: surfaceFromRatio(8245, 0.009),
    maxSteam: 2095,
    stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'NO. 1 COT (S)',
    capacity: 8245,
    heatingSurfaceM2: surfaceFromRatio(8245, 0.009),
    maxSteam: 2095,
    stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'NO. 2 COT (P)',
    capacity: 9951,
    heatingSurfaceM2: surfaceFromRatio(9951, 0.0086),
    maxSteam: 2420,
    stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'NO. 2 COT (S)',
    capacity: 9951,
    heatingSurfaceM2: surfaceFromRatio(9951, 0.0086),
    maxSteam: 2420,
    stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'NO. 3 COT (P)',
    capacity: 10011,
    heatingSurfaceM2: surfaceFromRatio(10011, 0.008),
    maxSteam: 2430,
    stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'NO. 3 COT (S)',
    capacity: 10011,
    heatingSurfaceM2: surfaceFromRatio(10011, 0.008),
    maxSteam: 2430,
    stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'NO. 4 COT (P)',
    capacity: 10011,
    heatingSurfaceM2: surfaceFromRatio(10011, 0.008),
    maxSteam: 2430,
    stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'NO. 4 COT (S)',
    capacity: 10011,
    heatingSurfaceM2: surfaceFromRatio(10011, 0.008),
    maxSteam: 2430,
    stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'NO. 5 COT (P)',
    capacity: 10011,
    heatingSurfaceM2: surfaceFromRatio(10011, 0.008),
    maxSteam: 2430,
    stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'NO. 5 COT (S)',
    capacity: 10011,
    heatingSurfaceM2: surfaceFromRatio(10011, 0.008),
    maxSteam: 2430,
    stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'NO. 6 COT (P)',
    capacity: 9658,
    heatingSurfaceM2: surfaceFromRatio(9658, 0.0078),
    maxSteam: 2345,
    stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'NO. 6 COT (S)',
    capacity: 9658,
    heatingSurfaceM2: surfaceFromRatio(9658, 0.0078),
    maxSteam: 2345,
    stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'SLOP TANK (P)',
    capacity: 2353,
    heatingSurfaceM2: surfaceFromRatio(2353, 0.0115),
    maxSteam: 650,
    stepRequirement: SLOP_TANK_STEP_REQUIREMENT,
  },
  {
    tankName: 'SLOP TANK (S)',
    capacity: 2353,
    heatingSurfaceM2: surfaceFromRatio(2353, 0.0115),
    maxSteam: 650,
    stepRequirement: SLOP_TANK_STEP_REQUIREMENT,
  },
];

export const DEFAULT_STEAM_TABLE_PROFILES: SteamTableExtractionConfig[] = [
  {
    fileNamePattern: /tsuneishi|106.?300|1230/i,
    label: 'HEATING TABLE (Crude Oil Tanker)',
    rows: HEATING_TABLE_TSUNEISHI_1230,
  },
];

export interface SteamTableExtractionResult {
  rows: SteamTableRowConfig[];
  sourceNotes: string[];
}

export function resolveSteamTableProfile(
  fileName: string,
  profiles: SteamTableExtractionConfig[] = DEFAULT_STEAM_TABLE_PROFILES,
): SteamTableExtractionConfig | null {
  return (
    profiles.find(
      (p) => p.fileNamePattern && p.fileNamePattern.test(fileName),
    ) ?? null
  );
}

export function extractSteamTableData(
  fileName: string,
  profiles: SteamTableExtractionConfig[] = DEFAULT_STEAM_TABLE_PROFILES,
): SteamTableExtractionResult | null {
  const profile = resolveSteamTableProfile(fileName, profiles);
  if (!profile) {
    return null;
  }
  const rows = profile.rows.filter((r) => !isTotalRow(r.tankName));

  return {
    rows,
    sourceNotes: [
      profile.label ?? 'Heating table',
      `Imported ${rows.length} tank row(s)`,
      'Tank Name, Capacity, Heating Surface (m²), Max Steam, Step Requirement auto-filled',
    ],
  };
}

export function isTotalRow(name: string): boolean {
  return /^total$/i.test(name.trim());
}

export function countHeatingTableDataRows(rows: HeatingTableDisplayRow[]): number {
  return rows.filter((row) => !row.isTotal && !isTotalRow(row.tankName)).length;
}

export function formatDisplaySum(values: string[]): string {
  const cleaned = values.map((value) => value.trim()).filter(Boolean);
  if (!cleaned.length) {
    return '';
  }

  const decimalPlaces = cleaned.map((value) => {
    const normalized = value.replace(/,/g, '');
    const fraction = normalized.split('.')[1];
    return fraction ? fraction.length : 0;
  });
  const maxDecimals = Math.max(...decimalPlaces, 0);
  const total = cleaned.reduce((sum, value) => sum + parseNumber(value), 0);

  if (maxDecimals === 0) {
    return String(Math.round(total));
  }

  return total
    .toFixed(maxDecimals)
    .replace(/(\.\d*?)0+$/, '$1')
    .replace(/\.$/, '');
}

export function appendTotalRowIfMissing(
  rows: HeatingTableDisplayRow[],
): HeatingTableDisplayRow[] {
  const dataRows = rows.filter((row) => !row.isTotal && !isTotalRow(row.tankName));
  const existingTotal = rows.find(
    (row) => row.isTotal || isTotalRow(row.tankName),
  );
  if (existingTotal) {
    return [
      ...dataRows,
      {
        ...existingTotal,
        tankName: 'TOTAL',
        isTotal: true,
        capacityDisplay:
          existingTotal.capacityDisplay ??
          formatDisplaySum(
            dataRows.map((row) => row.capacityDisplay ?? String(row.capacity ?? '')),
          ),
        heatingSurfaceDisplay:
          existingTotal.heatingSurfaceDisplay ??
          formatDisplaySum(
            dataRows.map(
              (row) =>
                row.heatingSurfaceDisplay ?? String(row.heatingSurfaceM2 ?? ''),
            ),
          ),
        maxSteamDisplay:
          existingTotal.maxSteamDisplay ??
          formatDisplaySum(
            dataRows.map((row) => row.maxSteamDisplay ?? String(row.maxSteam ?? '')),
          ),
      },
    ];
  }
  if (!dataRows.length) {
    return rows;
  }

  const sum = (values: Array<number | null | undefined>): number =>
    values.reduce((total: number, value) => total + (Number(value) || 0), 0);

  const capacityDisplays = dataRows.map(
    (row) => row.capacityDisplay ?? String(row.capacity ?? ''),
  );
  const heatingDisplays = dataRows.map(
    (row) => row.heatingSurfaceDisplay ?? String(row.heatingSurfaceM2 ?? ''),
  );
  const maxSteamDisplays = dataRows.map(
    (row) => row.maxSteamDisplay ?? String(row.maxSteam ?? ''),
  );

  return [
    ...dataRows,
    {
      tankName: 'TOTAL',
      capacity: sum(dataRows.map((row) => row.capacity)),
      heatingSurfaceM2: sum(dataRows.map((row) => row.heatingSurfaceM2)),
      maxSteam: sum(dataRows.map((row) => row.maxSteam)),
      capacityDisplay: formatDisplaySum(capacityDisplays),
      heatingSurfaceDisplay: formatDisplaySum(heatingDisplays),
      maxSteamDisplay: formatDisplaySum(maxSteamDisplays),
      isTotal: true,
    },
  ];
}

export function finalizeHeatingTableDisplayRows(
  rows: HeatingTableDisplayRow[],
): HeatingTableDisplayRow[] {
  const normalized = rows
    .map((row) => ({
      ...row,
      tankName: String(row.tankName ?? '').trim(),
      isTotal: row.isTotal || isTotalRow(String(row.tankName ?? '')),
    }))
    .filter((row) => {
      if (!row.tankName) {
        return false;
      }
      if (row.isTotal) {
        return true;
      }
      if (isGarbledTankName(row.tankName)) {
        return false;
      }
      const capacityValue =
        parseNumber(String(row.capacityDisplay ?? row.capacity ?? '')) ||
        (Number.isFinite(row.capacity) ? row.capacity : NaN);
      const surfaceValue = parseNumber(String(row.heatingSurfaceDisplay ?? ''));
      const steamValue = parseNumber(String(row.maxSteamDisplay ?? ''));
      if (TANK_NAME_PATTERN.test(row.tankName)) {
        return (
          (Number.isFinite(capacityValue) && capacityValue > 0) ||
          (Number.isFinite(surfaceValue) && surfaceValue > 0) ||
          (Number.isFinite(steamValue) && steamValue > 0)
        );
      }
      return Number.isFinite(capacityValue) && capacityValue > 0;
    });

  return appendTotalRowIfMissing(normalized);
}

export function toHeatingTableDisplayRows(
  rows: SteamTableRowConfig[],
): HeatingTableDisplayRow[] {
  return finalizeHeatingTableDisplayRows(
    rows.map((row) => ({
      ...row,
      isTotal: isTotalRow(row.tankName),
      tankName: isTotalRow(row.tankName) ? 'TOTAL' : row.tankName,
    })),
  );
}

const TANK_NAME_PATTERN =
  /^(NO\.?\s*\d+\s*(?:COT|PORT|C\.?\s*O\.?\s*T(?:K)?)\s*\([PS]\)|SLOP\s*T(?:ANK|K)\s*\([PS]\)|TOTAL)$/i;

/** Header fragments and OCR garbage — never valid tank names. */
export function isGarbledTankName(name: string): boolean {
  const trimmed = name.trim();
  if (!trimmed) {
    return true;
  }
  if (isTotalRow(trimmed)) {
    return false;
  }
  if (TANK_NAME_PATTERN.test(trimmed)) {
    return false;
  }
  if (/^[\d,.\-\]}\[{]+$/i.test(trimmed)) {
    return true;
  }
  if (/^(m3|m²|m2|kg|hr|kg\/hr)\]?$/i.test(trimmed)) {
    return true;
  }
  if (/m3\]?$|\[98|full\]|ratio/i.test(trimmed) && !/(?:NO|SLOP|COT|TANK|PORT)/i.test(trimmed)) {
    return true;
  }
  if (/[\]\[]/.test(trimmed) && !/(?:NO|SLOP|COT|TANK|PORT)/i.test(trimmed)) {
    return true;
  }
  if (trimmed.length <= 4 && !/^\d+$/.test(trimmed)) {
    return true;
  }
  return false;
}

/** Minimum bar: at least one named row with positive capacity. */
export function isUsableSteamTableRows(rows: SteamTableRowConfig[]): boolean {
  return rows.some(
    (row) =>
      (row.tankName?.trim().length ?? 0) > 1 &&
      Number.isFinite(row.capacity) &&
      row.capacity > 0,
  );
}

/** Rank extraction candidates — higher is better. */
export function scoreSteamTableRows(rows: SteamTableRowConfig[]): number {
  if (!rows.length) {
    return 0;
  }

  let score = 0;
  for (const row of rows) {
    const name = row.tankName?.trim() ?? '';
    if (!name || isTotalRow(name)) {
      continue;
    }
    if (TANK_NAME_PATTERN.test(name) && row.capacity >= 500) {
      score += 12;
    } else if (row.capacity > 0) {
      score += 6;
    }
    if (row.heatingSurfaceM2 != null && row.heatingSurfaceM2 > 0) {
      score += 2;
    }
    if (row.maxSteam != null && row.maxSteam > 0) {
      score += 2;
    }
  }
  return score;
}

export function pickBestSteamTableRows(
  candidates: SteamTableRowConfig[][],
): SteamTableRowConfig[] {
  let best: SteamTableRowConfig[] = [];
  let bestScore = 0;
  for (const rows of candidates) {
    const normalized = normalizeSteamTableRows(rows as any[]);
    const candidateScore = scoreSteamTableRows(normalized);
    if (candidateScore > bestScore) {
      bestScore = candidateScore;
      best = normalized;
    }
  }
  return best;
}

/** True when parsed rows look like a real heating table (not OCR garbage). */
export function isReliableSteamTableRows(rows: SteamTableRowConfig[]): boolean {
  if (!rows.length) {
    return false;
  }

  const validRows = rows.filter((row) => {
    const name = row.tankName?.trim() ?? '';
    return (
      TANK_NAME_PATTERN.test(name) &&
      Number.isFinite(row.capacity) &&
      row.capacity >= 500
    );
  });

  if (validRows.length < 3) {
    return false;
  }

  return validRows.length / rows.length >= 0.6;
}

function parseNumber(raw: string): number {
  const cleaned = raw.replace(/,/g, '').replace(/[^\d.-]/g, '');
  const n = Number(cleaned);
  return Number.isFinite(n) ? n : NaN;
}

function splitTableLine(line: string): string[] {
  if (line.includes('\t')) {
    return line.split('\t').map((p) => p.trim());
  }
  if (line.includes('|')) {
    return line.split('|').map((p) => p.trim());
  }
  return line.split(/[,;]+/).map((p) => p.trim());
}

function isHeatingSurfaceHeader(header: string): boolean {
  const h = header.toLowerCase();
  return (
    (h.includes('heating') && h.includes('surface')) ||
    (h.includes('grid') && h.includes('pipe')) ||
    (h.includes('surface') && (h.includes('m2') || h.includes('m²')))
  );
}

function isHeatingRatioHeader(header: string): boolean {
  const h = header.toLowerCase();
  return (
    (h.includes('heating') && h.includes('ratio')) ||
    h.includes('m2/m3') ||
    h.includes('m²/m3')
  );
}

function isMaxSteamHeader(header: string): boolean {
  const h = header.toLowerCase();
  return (
    (h.includes('max') && h.includes('steam')) ||
    (h.includes('steam') && h.includes('consumption')) ||
    /^max\.?\s*steam/i.test(h.trim()) ||
    (h.includes('kg') && h.includes('hr'))
  );
}

function isStepRequirementHeader(header: string): boolean {
  const h = header.toLowerCase();
  return (
    (h.includes('step') && h.includes('requirement')) ||
    h.includes('step req') ||
    (h.includes('heating') && h.includes('condition'))
  );
}

function findColumnIndexes(headerParts: string[]): {
  nameIdx: number;
  capacityIdx: number;
  heatingSurfaceIdx: number;
  maxSteamIdx: number;
  stepRequirementIdx: number;
} | null {
  const lower = headerParts.map((p) => p.toLowerCase());

  const nameIdx = lower.findIndex(
    (h) =>
      (h.includes('tank') && (h.includes('name') || h === 'tank')) ||
      h === 'name' ||
      h.includes('compartment') ||
      h.includes('tank no') ||
      /^no\.?$/i.test(h.trim()),
  );
  const capacityIdx = lower.findIndex(
    (h) =>
      h === 'capacity' ||
      h.includes('capacity') ||
      h.includes('m3') ||
      h.includes('m³') ||
      h.includes('98%'),
  );
  const heatingSurfaceIdx = lower.findIndex(
    (h) => isHeatingSurfaceHeader(h) && !isHeatingRatioHeader(h),
  );
  const maxSteamIdx = lower.findIndex((h) => isMaxSteamHeader(h));
  const stepRequirementIdx = lower.findIndex((h) => isStepRequirementHeader(h));

  if (nameIdx >= 0 && capacityIdx >= 0) {
    return {
      nameIdx,
      capacityIdx,
      heatingSurfaceIdx,
      maxSteamIdx,
      stepRequirementIdx,
    };
  }

  return null;
}

function resolveRowIndexes(
  parts: string[],
  indexes: {
    nameIdx: number;
    capacityIdx: number;
    heatingSurfaceIdx: number;
    maxSteamIdx: number;
    stepRequirementIdx: number;
  },
): {
  tankName: string;
  capacity: number;
  heatingSurfaceM2: number | null;
  maxSteam: number | null;
  stepRequirement: string | null;
} | null {
  const tankName = parts[indexes.nameIdx] ?? parts[0];
  const isTotal = isTotalRow(tankName);

  if (!tankName) {
    return null;
  }

  const capacity = parseNumber(parts[indexes.capacityIdx] ?? parts[1]);
  if (!isTotal && (!Number.isFinite(capacity) || capacity <= 0)) {
    return null;
  }

  let heatingSurfaceIdx = indexes.heatingSurfaceIdx;
  if (heatingSurfaceIdx < 0) {
    // Grid pipe (4 cols): name | capacity | surface | max steam
    if (parts.length === 4) {
      heatingSurfaceIdx = 2;
    } else if (parts.length >= 6) {
      // name | capacity | ratio | surface | max steam | step
      heatingSurfaceIdx = 3;
    } else if (parts.length >= 4) {
      heatingSurfaceIdx = 2;
    } else {
      heatingSurfaceIdx = 2;
    }
  }

  let maxSteamIdx = indexes.maxSteamIdx;
  if (maxSteamIdx < 0) {
    if (parts.length === 4) {
      maxSteamIdx = 3;
    } else if (parts.length >= 6) {
      maxSteamIdx = 4;
    } else if (parts.length >= 5) {
      maxSteamIdx = parts.length - 2;
    }
  }

  let stepRequirementIdx = indexes.stepRequirementIdx;
  if (stepRequirementIdx < 0) {
    stepRequirementIdx = parts.length - 1;
  }

  if (maxSteamIdx === heatingSurfaceIdx) {
    maxSteamIdx = -1;
  }

  const heatingSurfaceRaw = parts[heatingSurfaceIdx];
  const maxSteamRaw = maxSteamIdx >= 0 ? parts[maxSteamIdx] : undefined;
  const stepRequirementRaw = parts[stepRequirementIdx];

  const heatingSurfaceM2 = heatingSurfaceRaw
    ? parseNumber(heatingSurfaceRaw)
    : NaN;
  const maxSteam = maxSteamRaw ? parseNumber(maxSteamRaw) : NaN;

  const stepRequirement = stepRequirementRaw?.trim() ?? '';
  const looksNumericStep = stepRequirement && Number.isFinite(Number(stepRequirement));

  return {
    tankName: isTotal ? 'TOTAL' : tankName,
    capacity: Number.isFinite(capacity) ? capacity : 0,
    heatingSurfaceM2: Number.isFinite(heatingSurfaceM2) ? heatingSurfaceM2 : null,
    maxSteam: Number.isFinite(maxSteam) ? maxSteam : null,
    stepRequirement:
      stepRequirement && !looksNumericStep ? stepRequirement : null,
  };
}

function assignHeatingColumns(
  capacity: number,
  trailingNumbers: number[],
): { heatingSurfaceM2: number | null; maxSteam: number | null } {
  let heatingSurfaceM2: number | null = null;
  let maxSteam: number | null = null;

  const nums = trailingNumbers.filter((value) => Number.isFinite(value) && value > 0);

  for (const value of nums) {
    if (value > 0 && value < 0.2 && capacity > 0) {
      const derivedSurface = Math.round(capacity * value * 100) / 100;
      if (heatingSurfaceM2 == null && derivedSurface >= 5) {
        heatingSurfaceM2 = derivedSurface;
      }
    }
  }

  const dataValues = nums.filter((value) => !(value > 0 && value < 0.2));

  if (dataValues.length === 1) {
    const value = dataValues[0];
    if (value >= 5 && value <= 2500) {
      heatingSurfaceM2 = value;
    } else {
      maxSteam = value;
    }
  } else if (dataValues.length >= 2) {
    const sorted = [...dataValues].sort((a, b) => a - b);
    const surfaceCandidate = sorted.find((value) => value >= 5 && value <= 2500);
    if (surfaceCandidate != null) {
      heatingSurfaceM2 = surfaceCandidate;
    }
    const steamCandidate = [...sorted]
      .reverse()
      .find((value) => value !== heatingSurfaceM2);
    if (steamCandidate != null) {
      maxSteam = steamCandidate;
    }
  }

  return { heatingSurfaceM2, maxSteam };
}

/** Infer heating surface (m²) and max steam (kg/hr) from capacity and trailing table numbers. */
export function inferHeatingSurfaceAndMaxSteam(
  capacity: number,
  trailingNumbers: number[],
): { heatingSurfaceM2: number | null; maxSteam: number | null } {
  return assignHeatingColumns(capacity, trailingNumbers);
}

function extractTrailingNumbers(raw: string): number[] {
  return (raw.match(/[\d,]+(?:\.\d+)?/g) ?? [])
    .map(parseNumber)
    .filter((n) => Number.isFinite(n));
}

const TANK_NAME_IN_LINE =
  /((?:NO\.?\s*)?\d+\s*(?:PORT|COT|C\.?\s*O\.?\s*T(?:K)?)|SLOP\s*T(?:ANK|K)?)\s*\(\s*([PS])\s*\)/i;

function formatTankNameFromOcr(raw: string): string {
  const match = raw.match(TANK_NAME_IN_LINE);
  if (!match) {
    return raw.replace(/\s+/g, ' ').trim();
  }

  const segment = match[1].replace(/\s+/g, ' ').trim().toUpperCase();
  const side = match[2].toUpperCase();

  if (/^SLOP/i.test(segment)) {
    return `SLOP TANK (${side})`;
  }

  const numMatch = segment.match(/\d+/);
  const num = numMatch?.[0] ?? '';
  if (/PORT/i.test(segment)) {
    return `${num} PORT (${side})`;
  }
  if (/C\.?\s*O\.?\s*T/i.test(segment)) {
    return `NO. ${num} C.O.TK(${side})`;
  }
  return `NO. ${num} COT (${side})`;
}

/**
 * Multi-line OCR: tank name on one line, numeric columns on the next line(s).
 */
export function parseHeatingTableBlocks(content: string): SteamTableRowConfig[] {
  const lines = content
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 2);
  const rows: SteamTableRowConfig[] = [];
  const seen = new Set<string>();

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const nameMatch = line.match(TANK_NAME_IN_LINE);
    if (!nameMatch) {
      continue;
    }

    const tankName = formatTankNameFromOcr(nameMatch[0]);
    const key = normalizeTankName(tankName);
    if (seen.has(key)) {
      continue;
    }

    let numericBlob = line.slice(nameMatch.index! + nameMatch[0].length);
    for (
      let offset = 1;
      offset <= 3 && !extractTrailingNumbers(numericBlob).length;
      offset++
    ) {
      const nextLine = lines[i + offset];
      if (!nextLine || TANK_NAME_IN_LINE.test(nextLine)) {
        break;
      }
      numericBlob += ` ${nextLine}`;
    }

    const nums = extractTrailingNumbers(numericBlob);
    if (!nums.length) {
      continue;
    }

    const capacity = nums[0];
    const minCapacity = /slop/i.test(tankName) ? 50 : 100;
    if (capacity < minCapacity) {
      continue;
    }

    const { heatingSurfaceM2, maxSteam } = assignHeatingColumns(
      capacity,
      nums.slice(1),
    );

    seen.add(key);
    rows.push({
      tankName,
      capacity,
      heatingSurfaceM2,
      maxSteam,
      stepRequirement: /slop/i.test(tankName)
        ? SLOP_TANK_STEP_REQUIREMENT
        : CARGO_TANK_STEP_REQUIREMENT,
    });
  }

  return rows.sort((a, b) => a.tankName.localeCompare(b.tankName));
}

/**
 * Extract tank rows from noisy OCR / PDF text using heating-table row patterns.
 * Expects: capacity, optional ratio, heating surface (m²), max steam.
 */
export function parseHeatingTableRowPatterns(content: string): SteamTableRowConfig[] {
  const rows: SteamTableRowConfig[] = [];
  const seen = new Set<string>();
  const normalized = content.replace(/\s+/g, ' ');

  const cotPattern =
    /NO\.?\s*(\d+)\s*COT\s*\(\s*([PS])\s*\)((?:\s*[\d,]+(?:\.\d+)?)+)/gi;
  let match: RegExpExecArray | null;
  while ((match = cotPattern.exec(normalized)) !== null) {
    const tankName = `NO. ${match[1]} COT (${match[2].toUpperCase()})`;
    const nums = extractTrailingNumbers(match[3]);
    if (!nums.length) {
      continue;
    }
    const capacity = nums[0];
    const { heatingSurfaceM2, maxSteam } = assignHeatingColumns(
      capacity,
      nums.slice(1),
    );
    if (!seen.has(tankName) && capacity >= 500) {
      seen.add(tankName);
      rows.push({
        tankName,
        capacity,
        heatingSurfaceM2,
        maxSteam,
        stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
      });
    }
  }

  const slopPattern =
    /SLOP\s*T(?:ANK|K)\s*\(\s*([PS])\s*\)((?:\s*[\d,]+(?:\.\d+)?)+)/gi;
  while ((match = slopPattern.exec(normalized)) !== null) {
    const tankName = `SLOP TANK (${match[1].toUpperCase()})`;
    const nums = extractTrailingNumbers(match[2]);
    if (!nums.length) {
      continue;
    }
    const capacity = nums[0];
    const { heatingSurfaceM2, maxSteam } = assignHeatingColumns(
      capacity,
      nums.slice(1),
    );
    if (!seen.has(tankName) && capacity >= 100) {
      seen.add(tankName);
      rows.push({
        tankName,
        capacity,
        heatingSurfaceM2,
        maxSteam,
        stepRequirement: SLOP_TANK_STEP_REQUIREMENT,
      });
    }
  }

  const portPattern =
    /(?:NO\.?\s*)?(\d+)\s*PORT\s*\(\s*([PS])\s*\)((?:\s*[\d,]+(?:\.\d+)?)+)/gi;
  while ((match = portPattern.exec(normalized)) !== null) {
    const tankName = `${match[1]} PORT (${match[2].toUpperCase()})`;
    const nums = extractTrailingNumbers(match[3]);
    if (!nums.length) {
      continue;
    }
    const capacity = nums[0];
    const { heatingSurfaceM2, maxSteam } = assignHeatingColumns(
      capacity,
      nums.slice(1),
    );
    if (!seen.has(tankName) && capacity >= 100) {
      seen.add(tankName);
      rows.push({
        tankName,
        capacity,
        heatingSurfaceM2,
        maxSteam,
        stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
      });
    }
  }

  const cotkPattern =
    /NO\.?\s*(\d+)\s*C\.?\s*O\.?\s*T(?:K)?\.?\s*\(\s*([PS])\s*\)((?:\s*[\d,]+(?:\.\d+)?)+)/gi;
  while ((match = cotkPattern.exec(normalized)) !== null) {
    const tankName = `NO. ${match[1]} C.O.TK(${match[2].toUpperCase()})`;
    const nums = extractTrailingNumbers(match[3]);
    if (!nums.length) {
      continue;
    }
    const capacity = nums[0];
    const { heatingSurfaceM2, maxSteam } = assignHeatingColumns(
      capacity,
      nums.slice(1),
    );
    if (!seen.has(tankName) && capacity >= 100) {
      seen.add(tankName);
      rows.push({
        tankName,
        capacity,
        heatingSurfaceM2,
        maxSteam,
        stepRequirement: CARGO_TANK_STEP_REQUIREMENT,
      });
    }
  }

  return rows.sort((a, b) => a.tankName.localeCompare(b.tankName));
}

export function normalizeTankName(name: string): string {
  return name.replace(/\s+/g, ' ').trim().toUpperCase();
}

export function hasMaxSteamData(rows: SteamTableRowConfig[]): boolean {
  return rows.some((row) => {
    const value = row.maxSteam;
    return value != null && Number.isFinite(Number(value));
  });
}

/** Prefer extracted/OCR rows; append profile rows only for tanks missing from extraction. */
export function mergeSteamTableRows(
  base: SteamTableRowConfig[],
  enrich: SteamTableRowConfig[],
): SteamTableRowConfig[] {
  if (!enrich.length) {
    return base.map((row) => ({ ...row }));
  }

  const enrichByName = new Map(
    enrich.map((row) => [normalizeTankName(row.tankName), row]),
  );
  const baseByName = new Map(
    base.map((row) => [normalizeTankName(row.tankName), row]),
  );

  const mergedNames = new Set<string>();
  const merged: SteamTableRowConfig[] = [];

  for (const row of enrich) {
    const key = normalizeTankName(row.tankName);
    if (mergedNames.has(key)) {
      continue;
    }
    mergedNames.add(key);
    const profile = baseByName.get(key);
    merged.push(
      profile
        ? {
            ...profile,
            capacity: row.capacity || profile.capacity,
            heatingSurfaceM2:
              row.heatingSurfaceM2 ?? profile.heatingSurfaceM2 ?? null,
            maxSteam: row.maxSteam ?? profile.maxSteam ?? null,
            stepRequirement:
              row.stepRequirement || profile.stepRequirement || null,
          }
        : { ...row },
    );
  }

  for (const row of base) {
    const key = normalizeTankName(row.tankName);
    if (!mergedNames.has(key)) {
      mergedNames.add(key);
      merged.push({ ...row });
    }
  }

  return merged.sort((a, b) => a.tankName.localeCompare(b.tankName));
}

export function parseSteamTableRowsFromText(
  content: string,
): SteamTableRowConfig[] {
  return pickBestSteamTableRows([
    parseHeatingTableBlocks(content),
    parseHeatingTableRowPatterns(content),
    parseSteamTableText(content).filter((row) => !row.isTotal),
  ]);
}

export function parseHeatingTableDisplayFromText(
  content: string,
): HeatingTableDisplayRow[] {
  const parsedTable = parseSteamTableText(content);
  const tableTotal = parsedTable.find((row) => row.isTotal);
  const best = pickBestSteamTableRows([
    parseHeatingTableBlocks(content),
    parseHeatingTableRowPatterns(content),
    parsedTable.filter((row) => !row.isTotal),
  ]);
  return finalizeHeatingTableDisplayRows([
    ...toHeatingTableDisplayRows(best).filter((row) => !row.isTotal),
    ...(tableTotal ? [tableTotal] : []),
  ]);
}

export function parseGeminiHeatingTableResponse(
  text: string,
): SteamTableRowConfig[] {
  return parseGeminiHeatingTableDisplayResponse(text).filter((row) => !row.isTotal);
}

export function parseGeminiHeatingTableDisplayResponse(
  text: string,
): HeatingTableDisplayRow[] {
  try {
    const cleaned = text
      .trim()
      .replace(/^```json\s*/i, '')
      .replace(/```$/i, '');
    const parsed = JSON.parse(cleaned) as {
      rows?: unknown[];
      stepRequirement?: string;
      cargoTankStepRequirement?: string;
      slopTankStepRequirement?: string;
    };
    const cargoStep =
      parsed.cargoTankStepRequirement ??
      parsed.stepRequirement ??
      CARGO_TANK_STEP_REQUIREMENT;
    const slopStep =
      parsed.slopTankStepRequirement ?? SLOP_TANK_STEP_REQUIREMENT;

    return normalizeHeatingTableDisplayRows(
      (parsed.rows ?? []).map((row: any) => ({
        ...row,
        stepRequirement:
          row.stepRequirement ??
          (/slop/i.test(String(row.tankName ?? row.name ?? ''))
            ? slopStep
            : cargoStep),
      })),
    );
  } catch {
    return [];
  }
}

/**
 * Parse CSV/TSV/text exports of a heating table.
 * Columns: Name, Capacity, Heating Surface (m²) [Grid Pipe], …, Max Steam, Step Requirement.
 */
export function parseSteamTableText(content: string): HeatingTableDisplayRow[] {
  const lines = content
    .split(/\r?\n/)
    .map((l) => l.trim())
    .filter((l) => l.length > 0);

  if (lines.length < 2) {
    return [];
  }

  const rows: HeatingTableDisplayRow[] = [];
  let indexes = {
    nameIdx: 0,
    capacityIdx: 1,
    heatingSurfaceIdx: 2,
    maxSteamIdx: -1,
    stepRequirementIdx: -1,
  };
  let dataStart = 0;

  const firstParts = splitTableLine(lines[0]);
  const headerMatch = findColumnIndexes(firstParts);
  if (headerMatch) {
    indexes = headerMatch;
    dataStart = 1;
  } else if (
    firstParts.join(' ').toLowerCase().includes('tank') &&
    lines.length > 2
  ) {
    const secondParts = splitTableLine(lines[1]);
    const secondMatch = findColumnIndexes(secondParts);
    if (secondMatch) {
      indexes = secondMatch;
      dataStart = 2;
    } else {
      dataStart = 1;
    }
  }

  for (let i = dataStart; i < lines.length; i++) {
    const parts = splitTableLine(lines[i]);
    if (parts.length < 2) {
      continue;
    }

    const parsed = resolveRowIndexes(parts, indexes);
    if (!parsed) {
      continue;
    }

    rows.push({
      tankName: parsed.tankName,
      capacity: parsed.capacity,
      heatingSurfaceM2: parsed.heatingSurfaceM2,
      maxSteam: parsed.maxSteam,
      stepRequirement: parsed.stepRequirement,
      isTotal: isTotalRow(parsed.tankName),
    });
  }

  return finalizeHeatingTableDisplayRows(rows);
}

function resolveHeatingSurfaceM2(
  raw: Record<string, unknown>,
  capacity: number,
): number | null {
  const direct =
    raw['heatingSurfaceM2'] ??
    raw['heatingSurface'] ??
    raw['heating_surface_m2'] ??
    raw['heating_surface'];
  if (direct != null && direct !== '') {
    const n = Number(direct);
    return Number.isFinite(n) ? n : null;
  }

  const ratio = raw['heatingRatio'] ?? raw['heating_ratio'];
  if (ratio != null && ratio !== '' && capacity > 0) {
    const n = Number(ratio);
    if (Number.isFinite(n) && n > 0 && n < 1) {
      return surfaceFromRatio(capacity, n);
    }
  }

  return null;
}

function resolveMaxSteam(raw: Record<string, unknown>): number | null {
  const value =
    raw['maxSteam'] ??
    raw['maxSteamKgHr'] ??
    raw['maxSteamMt'] ??
    raw['max_steam'];
  if (value == null || value === '') {
    return null;
  }
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function mapRawToSteamTableRow(raw: Record<string, unknown>): HeatingTableDisplayRow {
  const tankName = String(raw['tankName'] ?? raw['name'] ?? '').trim();
  const capacityRaw = String(
    raw['tank_capacity'] ??
      raw['capacity'] ??
      raw['tankCapacityM3'] ??
      raw['capacityM3'] ??
      raw['capacityMt'] ??
      '',
  ).trim();
  const heatingSurfaceRaw = String(
    raw['heating_surface'] ??
      raw['heatingSurfaceM2'] ??
      raw['heatingSurface'] ??
      raw['heating_surface_m2'] ??
      '',
  ).trim();
  const maxSteamRaw = String(
    raw['max_steam_consumption'] ??
      raw['maxSteam'] ??
      raw['maxSteamKgHr'] ??
      raw['maxSteamMt'] ??
      raw['max_steam'] ??
      '',
  ).trim();
  const capacity = Number(capacityRaw.replace(/,/g, '')) || 0;
  const isTotal = isTotalRow(tankName);
  return {
    tankName: isTotal ? 'TOTAL' : tankName,
    capacity,
    heatingSurfaceM2: resolveHeatingSurfaceM2(raw, capacity),
    maxSteam: resolveMaxSteam(raw),
    capacityDisplay: capacityRaw,
    heatingSurfaceDisplay: heatingSurfaceRaw,
    maxSteamDisplay: maxSteamRaw,
    stepRequirement:
      raw['stepRequirement'] != null && raw['stepRequirement'] !== ''
        ? (raw['stepRequirement'] as string | number)
        : null,
    isTotal,
  };
}

/** Map legacy JSON/CSV keys into display rows (keeps TOTAL when present). */
export function normalizeHeatingTableDisplayRows(
  raw: unknown[],
): HeatingTableDisplayRow[] {
  return finalizeHeatingTableDisplayRows(
    raw
      .filter((row): row is Record<string, unknown> => !!row && typeof row === 'object')
      .map((row) => mapRawToSteamTableRow(row)),
  );
}

/** Map legacy JSON/CSV keys into SteamTableRowConfig (data rows only). */
export function normalizeSteamTableRows(raw: any[]): SteamTableRowConfig[] {
  return normalizeHeatingTableDisplayRows(raw).filter((row) => !row.isTotal);
}
